import { isDef, isNotNullAndUnDef, isString, isNumber, isArray } from '@/utils/is';
import { OPTIONS_TYPE, TIME_PERIOD_LENGTH } from '@/enums';

/**
 * 表单数据赋值
 * @param formConfig 表单配置对象
 * @param data 数据对象
 * @param config 配置对象
 */
export function formDataAssign(formConfig, data, config = {
  is_init_backup_value: false,
}) {
  for (const key in formConfig) {
    // 判断表单对象和数据对象是否存在
    if (isDef(formConfig[key]) && isNotNullAndUnDef(data[key])) {
      const item = formConfig[key];
      // 判断是否存在子级
      if (item.children) {
        formDataAssign(item.children, data[key], config);
      } else {
        item.value = data[key];
        (config.is_init_backup_value && item.hasOwnProperty('backup_value')) && (item.backup_value = item.value);
      }
    }
  }
}

// 复选框根据不限过滤
export function checkboxFilterNone(list, ident = null) {
  let temp_list = list;
  // 无值时默认选中【不限】
  if (!temp_list.length && ident) {
    temp_list.push(ident);
    return temp_list;
  }
  const index = temp_list.findIndex((val) => {
    return val === ident;
  });
  if (index !== -1 && (temp_list.length === 1 || index !== 0)) {
    temp_list = temp_list.filter((item) => {
      return item === ident;
    });
  } else {
    temp_list = temp_list.filter((item) => {
      return item !== ident;
    });
  }
  return temp_list;
}

/**
 * 获取字符长度
 * @param {string} str 要检测的字符串
 */
export function getLengthOfString(str) {
  if (!str || str.length === 0) {
    return 0;
  }
  let len = 0;
  for (let i = 0; i < str.length; i++) {
    /** 识别中文字符则长度+2，其余字符长度+1 */
    if (str.charAt(i).match(/[\u4e00-\u9fa5]/g) != null) {
      len += 2;
    } else {
      len += 1;
    }
  }
  return len;
}

/**
 * 校验字符长度
 * @param {string} filed_label 字段的文本
 * @param {number} maxlength 字符允许最大的长度
 * @returns {Function}
 */
export function validatorLengthOfString(filed_label, maxlength) {
  return (rule, value, callback) => {
    if (getLengthOfString(value) <= maxlength) {
      callback();
    } else {
      callback(new Error(`${filed_label}最大长度限制${maxlength}个字符，一个中文两个字符，其它一个字符`));
    }
  };
}

/** 根据游戏ID匹配对应游戏信息 */
export function findGameById(games, id, result) {
  if (!Array.isArray(games) || typeof id !== 'number' || typeof result !== 'object') {
    return;
  }
  for (let index = 0; index < games.length; index++) {
    const game = games[index];
    if (id === game.id) {
      Object.assign(result, { id: game.id, name: game.name });
      break;
    }
    if (game.children) {
      findGameById(game.children, id, result);
    }
  }
}

/**
 * 表单配置类型修改
 * @param {Object} form 表单配置对象
 * @param {string} type 表单组件类型 - 默认为label
 * @example <caption>使用示例</caption>
 * // return { key_1: { label: 'label-1', type: 'label', value: '' }, key_2: { label: 'label-2', value: '' } };
 * setFormType({
 *   key_1: {
 *     label: 'label-1',
 *     type: 'input',
 *     value: '',
 *   }
 *   key_2: {
 *     label: 'label-2',
 *     value: '',
 *   }
 * });
 */
export function setFormType(form, type = 'label'){
  Object.keys(form).forEach(key => {
    if (form[key]) {
      const item = form[key];
      if (item.children) {
        setFormType(item.children);
      } else if (item.type) {
        item.type = type;
      }
    }
  });
}

/**
 * 选项结构配置项类型
 * @typedef {Object} OptionsConfig
 * @property {string} equal_sign 同级连接符 - 默认“、”
 */

/**
 * 通过选项值从选项中获取选项名称
 * @param {Object} data 参数
 * @param {Object[]} data.options 选项
 * @param {(number|string|boolean|Array<number|string|boolean>)} data.value 选项值
 * @param {string} data.label_key 选项名称的key
 * @param {string} data.value_key 选项值的key
 * @param {OptionsConfig} data.config 选项结构配置项
 * @returns {string} 选项名称
 * @example <caption>使用示例</caption>
 * // return label-1#label-2;
 * getLabelsByOptions({
 *   options: [{ l: 'label-1', v: 'value-1' }, { l: 'label-2', v: 'value-2' }],
 *   value: ['value-1', 'value-2'],
 *   label_key: 'l',
 *   value_key: 'v',
 *   config: { equal_sign: '#' },
 * });
 */
export function getLabelsByOptions({ options = [], value = '', label_key = 'label', value_key = 'value', config = {}}){
  const temp_val = isArray(value) ? value : [value];
  if (!options.length || !temp_val.length) {
    return '-';
  }
  const { equal_sign = '、' } = config;
  return temp_val.map(val => options.find(opts => val === opts[value_key])?.[label_key] || '-').join(equal_sign);
}

/**
 * 树结构配置项类型
 * @typedef {Object} TreeConfig
 * @property {boolean} multiple 是否多选 - 默认单选
 * @property {boolean} emitPath 是否返回父节点 - 默认返回父节点
 * @property {string} equal_sign 同级连接符 - 默认“、”
 * @property {string} unequal_sign 父子级连接符 - 默认“/”
 */

/**
 * 通过选项值从树结构选项中获取选项名称
 * @param {Object} data 参数
 * @param {Object[]} data.options 树结构选项
 * @param {(string|number|Array<string|number>|Array<Array<string|number>>)} data.value 选项值
 * @param {string} data.label_key 选项名称的key
 * @param {string} data.value_key 选项值的key
 * @param {string} data.children_key 子节点的key
 * @param {TreeConfig} data.config 树结构配置项
 * @returns {string} 选项名称
 * @example <caption>后补说明文档，占位</caption>
 */
export function getLabelsByTreeOptions({
  options = [],
  value = '',
  label_key = 'label',
  value_key = 'value',
  children_key = 'children',
  config = {},
}){
  const {
    multiple: is_multiple = false,
    emitPath: is_emit_path = true,
    equal_sign = '、',
    unequal_sign = '/',
  } = config;

  /** 是否为number或string且有值 */
  const is_single = isNumber(value) || (isString(value) && value);
  /** 是否为一维数组且有值 */
  const is_array = isArray(value) && !!value.length && value.every(v => !isArray(v));
  /** 是否为二维数组且有值 */
  const is_matrix = isArray(value) && value.every(v => isArray(v) && v.length);
  /** 单选且仅返回子节点 - 单个值 */
  const is_single_child = !is_multiple && !is_emit_path && is_single;
  /** 单选同时返回父节点 - 一维数组 */
  const is_single_parent = !is_multiple && is_emit_path && is_array;
  /** 多选且仅返回子节点 - 一维数组 */
  const is_multiple_child = is_multiple && !is_emit_path && is_array;
  /** 多选同时返回父节点 - 二维数组 */
  const is_multiple_parent = is_multiple && is_emit_path && is_matrix;
  /** 选项值是否有效 */
  const is_valid_value = is_single_child || is_single_parent || is_multiple_child || is_multiple_parent;

  // 效验选项和选项值是否有效
  if (!options.length && !is_valid_value) {
    return '-';
  }

  /**
   * 通过选项值获取选项中的选项名称
   * @param {Object[]} opts 选项
   * @param {(string|number)} val 选项值
   * @returns {string} 选项名称
   */
  const getLabel = (opts, val) => {
    for (const item of opts) {
      if (item[value_key] === val) {
        return item[label_key] || '-';
      }
      if (item[children_key]) {
        const label = getLabel(item[children_key], val);
        if (label !== '-') {
          return label;
        }
      }
    }
    return '-';
  };

  let result = '-';

  is_single_child && (result = getLabel(options, value));
  is_single_parent && (result = value.map(val => getLabel(options, val)).join(unequal_sign));
  is_multiple_child && (result = value.map(val => getLabel(options, val)).join(equal_sign));
  is_multiple_parent && (result = value.map(val => (val || []).map(v => getLabel(options, v)).join(unequal_sign)).join(equal_sign));

  return result;
}

/**
 * 关联配置类型
 * @typedef {Object} AssociatedMap
 * @property {string} type 类型 - label：取父级label；options：取父级options中的label
 * @property {string} unlimit_key 代表【不限】的字段名称
 * @property {string[]} associated_keys 关联的子级列表
 * @property {Record<string, any>} associated_options_map 子级与父级options的对应关系，type = options时有效且必填
 */

/**
 * 表单配置转化为表单预览配置
 * @param {Object} data 参数
 * @param {Object} data.form_config 表单配置
 * @param {Record<string, AssociatedMap>} data.associated_map 关联配置
 * @example <caption>后补说明文档，占位</caption>
 */
export function transformFormToPreview({
  form_config = {},
  associated_map = {},
}) {
  Object.keys(form_config).forEach(key => {
    if (form_config[key]) {
      const item = form_config[key];

      // 关联配置处理逻辑
      if (associated_map[key]) {
        const { type = 'label', unlimit_key, associated_keys = [], associated_options_map = {}} = associated_map[key];
        const item_val = isArray(item.value) ? item.value : [item.value];
        !item_val.includes(unlimit_key || OPTIONS_TYPE.UNLIMITED) && (() => {
          item.hidden = true;
          associated_keys.forEach(associated_key => {
            if (form_config[associated_key]) {
              type === 'label' && (form_config[associated_key].label = item.label);
              (type === 'options' && associated_options_map[associated_key]) && (form_config[associated_key].label = item.options.find(opts => opts?.value === associated_options_map[associated_key])?.label || item.label);
            }
          });
        })();
      }

      // 仅对公共表单组件中包含的表单类型有效
      const map_tuple = [
        [
          () => item.type === 'select' || item.type === 'radio-group' || item.type === 'checkbox-group',
          () => (item.value = getLabelsByOptions({ options: item.options, value: item.value })),
        ],
        [
          () => item.type === 'cascader',
          () => (item.value = getLabelsByTreeOptions({ options: item.options, value: item.value, config: { ...(item?.props?.props || {}) }})),
        ],
        [
          () => item.type === 'date-picker',
          () => (item.value = isArray(item.value) ? (item.value.length ? item.value.join(' ~ ') : '-') : (item.value || '-')),
        ],
        [
          () => item.type === 'checkbox',
          () => {
            const { trueLabel, falseLabel } = item?.props || {};
            const is_custom = isDef(trueLabel) && isDef(falseLabel);
            item.value = is_custom ? (item.value === trueLabel ? '是' : '否') : (item.value ? '是' : '否');
          },
        ],
        [
          () => item.type === 'switch',
          () => {
            const { activeValue, inactiveValue } = item?.props || {};
            const is_custom = isDef(activeValue) && isDef(inactiveValue);
            item.value = is_custom ? (item.value === activeValue ? '是' : '否') : (item.value ? '是' : '否');
          },
        ],
        [
          () => item.type === 'tree-shuttle',
          () => (item.value = getLabelsByTreeOptions({ options: item.options, value: item.value, config: { ...{ multiple: true, emitPath: false }, ...(item?.props || {}) }})),
        ],
        [
          () => item.type === 'form-tags',
          () => {
            const is_column = item?.props?.direction === 'column';
            item.value = item.value.length ? item.value.join(is_column ? '<br>' : '、') : '-';
          },
        ],
        [
          () => item.type === 'form-range',
          () => {
            const { formType = 'select', startOptions = [], endOptions = [], startUnit, endUnit } = item?.props || {};
            if (formType === 'select') {
              item.value.start = getLabelsByOptions({ options: startOptions, value: item.value.start });
              item.value.end = getLabelsByOptions({ options: endOptions, value: item.value.end });
            }
            const { start, end } = item.value;
            item.value = [`${startUnit ? `${start}${startUnit}` : start}`, `${endUnit ? `${end}${endUnit}` : end}`].join(' ~ ');
          },
        ],
      ];
      const target = map_tuple.find(([m]) => m());
      target && target[1]();

      // 判断是否存在子级
      if (item.children) {
        transformFormToPreview({ form_config: item.children, associated_map });
      }
    }
  });
  // 设置表单配置类型为label
  setFormType(form_config);
}

/**
 * 转化树结构的key，仅保留且统一为label、value和children
 * @param {Object} data 参数
 * @param {Object[]} data.options 树结构选项
 * @param {string} data.label_key 选项名称的key
 * @param {string} data.value_key 选项值的key
 * @param {string} data.children_key 子节点的key
 * @returns {Object[]} 转化后的树结构选项
 * @example <caption>使用示例</caption>
 * // return [{ label: 'label-1', value: 'value-1', children: [{ label: 'label-2', value: 'value2' }] }];
 * transformTreeKey({
 *   options: [{ l: 'label-1', v: 'value-1', child: [{ l: 'label-2', v: 'value-2', child: [] }] }],
 *   label_key: 'l',
 *   value_key: 'v',
 *   children_key: 'child',
 * });
 */
export function transformTreeKey({ options = [], label_key = 'label', value_key = 'value', children_key = 'children' }) {
  return options.map(opts => ({
    label: opts[label_key],
    value: opts[value_key],
    ...((opts[children_key] && opts[children_key].length) && { children: transformTreeKey({ options: opts[children_key], label_key, value_key, children_key }) }),
  }));
}

/**
 * 时间选项转化为时间序列
 * @param {number} start 开始时间 - 0 ~ 23
 * @param {number} end 结束时间 - 1 ~ 24
 * @param {number} is_hour 以小时为粒度
 */
export function transformSelectedTimeToTimeSeries(start, end, is_hour = false) {
  if (start > end) {
    return;
  }

  const config = {
    length: is_hour ? TIME_PERIOD_LENGTH.HOUR : TIME_PERIOD_LENGTH.HALF_HOUR,
    multiplicand: is_hour ? 1 : 2,
  };

  let schedule = '';
  for (let i = 0; i < config.length; i++) {
    const day_index = i % 48;
    if (day_index >= start * config.multiplicand && day_index < end * config.multiplicand) {
      schedule += '1';
    } else {
      schedule += '0';
    }
  }
  return schedule;
}
